import React from "react";
import { Space } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowTrendUp } from "@fortawesome/free-solid-svg-icons";
import { ThemeProvider as StyledThemeProvider, DefaultTheme } from "styled-components";
import DateRangePicker from "../../../commonComponents/DateRangePicker";
import DateSelector from "../../../commonComponents/DateSelector";
import ChartSelection from "../../../commonComponents/ChartSelection";
import KeyMetricsSelector from "../../../commonComponents/KeyMetricSelector";
import BarLineChart from "../../../commonComponents/BarLineChart";
import {
  ChartContainer,
  ChartTitle,
  FilterContainer,
  MetricsContainer,
  MetricBadge,
  MetricIcon,
  MetricLabel,
  MetricValue,
  ChartWrapper,
  ChartSpaceWrapper,
} from "./styles";
import { ItemSalesChartProps } from "./constants";
import { useTheme } from "../../../context/ThemeContext";
import { addCommasInPrices } from "./constants";

const ItemSalesChart: React.FC<ItemSalesChartProps> = ({
  data,
  chartType,
  metricOption,
  selectedDateRange,
  onDateRangeChange,
  onDateSelect,
  onMetricChange,
  onChartTypeChange,
  keyMetrics,
  isLoading,
}) => {
  const { isDarkMode } = useTheme();

  const themeMode: "light" | "dark" = isDarkMode ? "dark" : "light";
  const styledTheme = { mode: themeMode } as DefaultTheme;

  return (
    <StyledThemeProvider theme={styledTheme}>
      <ChartContainer>
        <ChartSpaceWrapper direction="vertical" size="middle">
          <ChartTitle level={4}>Revenue Over Time</ChartTitle>
          
          <FilterContainer>
            <DateRangePicker
              selectedDateRange={selectedDateRange}
              onDateRangeChange={onDateRangeChange}
            />
            <DateSelector onDateSelect={onDateSelect} />
            <KeyMetricsSelector
              options={[
                { label: "Revenue", value: "totalRevenue" },
                { label: "Times Item Sold", value: "totalTimesSold" },
              ]}
              onSelectionChange={(option) => onMetricChange(option as any)}
            />
            <ChartSelection onChange={(type) => onChartTypeChange(type as any)} />
          </FilterContainer>

          <MetricsContainer>
            <Space align="center" wrap size="large">
              <MetricBadge align="center" color="#52c41a">
                <MetricIcon color="#52c41a">
                  <FontAwesomeIcon icon={faArrowTrendUp} />
                </MetricIcon>
                <MetricLabel>Total :</MetricLabel>
                <MetricValue strong color="#52c41a">
                  ${addCommasInPrices(keyMetrics?.totalRevenue || "0.00")}
                </MetricValue>
              </MetricBadge>
              <MetricBadge align="center" color="#1890ff">
                <MetricIcon color="#1890ff">
                  <FontAwesomeIcon icon={faArrowTrendUp} />
                </MetricIcon>
                <MetricLabel>Best Item by Revenue :</MetricLabel>
                <MetricValue strong color="#1890ff">
                  ${addCommasInPrices(keyMetrics?.bestCategoryByRevenue || "0.00")}
                </MetricValue>
              </MetricBadge>
              <MetricBadge align="center" color="#fa8c16">
                <MetricIcon color="#fa8c16">
                  <FontAwesomeIcon icon={faArrowTrendUp} />
                </MetricIcon>
                <MetricLabel>Best Item by Units Sold :</MetricLabel>
                <MetricValue strong color="#fa8c16">
                  {keyMetrics?.bestCategoryByUnitsSold || 0}
                </MetricValue>
              </MetricBadge>
              <MetricBadge align="center" color="#722ed1">
                <MetricIcon color="#722ed1">
                  <FontAwesomeIcon icon={faArrowTrendUp} />
                </MetricIcon>
                <MetricLabel>Average Revenue per Item :</MetricLabel>
                <MetricValue strong color="#722ed1">
                  ${addCommasInPrices(keyMetrics?.averageRevenue || "0.00")}
                </MetricValue>
              </MetricBadge>
            </Space>
          </MetricsContainer>

          <ChartWrapper>
            <BarLineChart
              data={data}
              chartType={chartType}
              xKey={"item"}
              yKey={metricOption}
              valueKey={metricOption}
              isLoading={isLoading}
            />
          </ChartWrapper>
        </ChartSpaceWrapper>
      </ChartContainer>
    </StyledThemeProvider>
  );
};

export default ItemSalesChart;
